import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import styled, { keyframes, css } from 'styled-components'
import { toast } from 'react-toastify'
import AddRoundedIcon           from '@mui/icons-material/AddRounded'
import SearchRoundedIcon        from '@mui/icons-material/SearchRounded'
import WarningAmberRoundedIcon  from '@mui/icons-material/WarningAmberRounded'
import HistoryRoundedIcon       from '@mui/icons-material/HistoryRounded'
import PrintRoundedIcon         from '@mui/icons-material/PrintRounded'
import EditRoundedIcon          from '@mui/icons-material/EditRounded'
import TaskAltRoundedIcon       from '@mui/icons-material/TaskAltRounded'
import ComplianceShell from 'features/compliance/ComplianceShell'
import { listPermits, createPermit, updatePermit, transitionPermit, uploadPermitRamsFile, downloadPermitRams, attachPermitRams } from 'services/permitService'
import { listContractors } from 'services/contractorService'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import SearchableSelect from 'components/SearchableSelect'
import { hideEmail } from 'utils/user'

const STATUS_META = {
  draft:          { label: 'Draft',          tint: '#9aa0aa', hint: 'Not yet submitted for review.' },
  pending_review: { label: 'Pending review', tint: '#d97706', hint: 'Waiting for someone to review and approve it.' },
  approved:       { label: 'Approved',       tint: '#33becc', hint: 'Approved — can be activated when the work starts.' },
  active:         { label: 'Active',         tint: '#15803d', hint: 'Work is authorised and currently under way.' },
  suspended:      { label: 'Suspended',      tint: '#d97706', hint: 'Work has been paused.' },
  expired:        { label: 'Expired',        tint: '#b00020', hint: 'The valid dates have passed.' },
  closed:         { label: 'Closed',         tint: '#7a7a83', hint: 'Work finished and the permit was closed out.' },
  cancelled:      { label: 'Cancelled',      tint: '#b00020', hint: 'Cancelled and no longer valid.' },
  rams_attached:  { label: 'RAMS attached',  tint: '#1f8c97', hint: 'A RAMS method statement has been attached.' },
}

const TRANSITION_LABEL = {
  pending_review: 'Submit for review',
  approved:       'Approve',
  active:         'Activate',
  suspended:      'Suspend',
  closed:         'Close out',
  cancelled:      'Cancel',
  draft:          'Back to draft',
}

const transitionLabelFor = (permit, next) => {
  if (next === 'active' && permit.status === 'suspended') return 'Resume'
  return TRANSITION_LABEL[next] || next
}

const RAG = {
  critical: { label: 'Critical',  fg: '#b00020', bg: '#fdecea', hint: 'The most serious level — needs urgent attention.' },
  high:     { label: 'High risk', fg: '#c2410c', bg: '#ffedd5', hint: 'Higher-hazard work — a RAMS method statement is required before the permit can be approved.' },
  medium:   { label: 'Medium',    fg: '#d97706', bg: '#fef3c7', hint: 'A moderate level.' },
  low:      { label: 'Standard',  fg: '#15803d', bg: '#dcfce7', hint: 'Standard work — no elevated controls required.' },
}

const SORTS = [
  { key: 'status',     label: 'Status',     title: 'status' },
  { key: 'title',      label: 'Permit',     title: 'permit title' },
  { key: 'activity',   label: 'Activity',   title: 'activity type' },
  { key: 'contractor', label: 'Contractor', title: 'contractor' },
  { key: 'validFrom',  label: 'Valid from', title: 'start date' },
  { key: 'risk',       label: 'Risk',       title: 'risk level' },
]

const PAGE = 20

const sortValue = (p, key) => {
  switch (key) {
    case 'status':     return (STATUS_META[p.status]?.label || p.status || '').toLowerCase()
    case 'title':      return (p.title || p.reference || '').toLowerCase()
    case 'activity':   return (p.activityTypeLabel || '').toLowerCase()
    case 'contractor': return (p.contractor?.name || '').toLowerCase()
    case 'validFrom':  return p.validFrom ? new Date(p.validFrom).getTime() : null
    case 'risk':       return p.isHighRisk ? 1 : 0
    default:           return ''
  }
}

const PermitsPage = ({ bpId: bpIdProp, embedded = false, onTabChange, chromeless = false } = {}) => {
  const routeParams = useParams()
  const bpId = bpIdProp ?? routeParams.bpId
  const [loading, setLoading] = useState(true)
  const [permits, setPermits] = useState([])
  const [activityTypes, setActivityTypes] = useState({})
  const [highRiskActivities, setHighRiskActivities] = useState([])
  const [contractors, setContractors] = useState([])
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState(null)
  const [historyTarget, setHistoryTarget] = useState(null)
  const [busy, setBusy] = useState(null)
  const [attachingRamsId, setAttachingRamsId] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [activityFilter, setActivityFilter] = useState('all')
  const [riskFilter, setRiskFilter] = useState('all')
  const [sortKey, setSortKey] = useState('validFrom')
  const [sortDir, setSortDir] = useState('desc')
  const [visibleCount, setVisibleCount] = useState(PAGE)

  const [searchParams] = useSearchParams()
  const deepLinkPermitId = searchParams.get('permit')
  const deepLinkFiredRef = useRef(false)
  const rowRefs = useRef({})
  const [highlightedPermitId, setHighlightedPermitId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [permRes, conRes] = await Promise.allSettled([
        listPermits(bpId),
        listContractors(),
      ])
      if (permRes.status === 'fulfilled') {
        const payload = permRes.value?.data?.data || {}
        setPermits(payload.permits || [])
        setActivityTypes(payload.activityTypes || {})
        setHighRiskActivities(payload.highRiskActivities || [])
      }
      if (conRes.status === 'fulfilled') {
        setContractors(conRes.value?.data?.data?.contractors || [])
      }
    } catch (e) {
      toast.error('Could not load permits.')
    } finally {
      setLoading(false)
    }
  }, [bpId])

  useEffect(() => { load() }, [load])

  const statusOptions = useMemo(() => {
    const set = new Set(permits.map(p => p.status).filter(Boolean))
    return [...set]
      .map(s => ({ value: s, label: STATUS_META[s]?.label || s, color: STATUS_META[s]?.tint }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [permits])

  const activityOptions = useMemo(() => {
    const map = new Map()
    permits.forEach(p => {
      if (p.activityType && !map.has(p.activityType)) {
        map.set(p.activityType, p.activityTypeLabel || activityTypes[p.activityType] || p.activityType)
      }
    })
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [permits, activityTypes])

  const riskPresent = useMemo(() => ({
    high: permits.some(p => p.isHighRisk),
    low: permits.some(p => !p.isHighRisk),
  }), [permits])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const rows = permits.filter(p => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false
      if (activityFilter !== 'all' && p.activityType !== activityFilter) return false
      if (riskFilter === 'high' && !p.isHighRisk) return false
      if (riskFilter === 'low' && p.isHighRisk) return false
      if (q) {
        const hay = [
          p.reference,
          p.title,
          p.activityTypeLabel,
          p.contractor?.name,
          p.contractor?.company,
          STATUS_META[p.status]?.label,
        ].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
    return rows.sort((a, b) => {
      const av = sortValue(a, sortKey)
      const bv = sortValue(b, sortKey)
      const aEmpty = av === null || av === undefined || av === ''
      const bEmpty = bv === null || bv === undefined || bv === ''
      if (aEmpty && bEmpty) return 0
      if (aEmpty) return 1
      if (bEmpty) return -1
      if (av < bv) return sortDir === 'asc' ? -1 : 1
      if (av > bv) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [permits, search, statusFilter, activityFilter, riskFilter, sortKey, sortDir])

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount])

  useEffect(() => { setVisibleCount(PAGE) }, [search, statusFilter, activityFilter, riskFilter, sortKey, sortDir])

  useEffect(() => {
    if (!deepLinkPermitId || deepLinkFiredRef.current || loading) return
    deepLinkFiredRef.current = true
    const idx = filtered.findIndex(p => String(p.id) === String(deepLinkPermitId))
    if (idx === -1) return
    if (idx >= visibleCount) setVisibleCount(Math.ceil((idx + 1) / PAGE) * PAGE)
    setHighlightedPermitId(deepLinkPermitId)
  }, [deepLinkPermitId, loading, filtered, visibleCount])

  useEffect(() => {
    if (!highlightedPermitId) return
    rowRefs.current[highlightedPermitId]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const timer = setTimeout(() => setHighlightedPermitId(null), 2000)
    return () => clearTimeout(timer)
  }, [highlightedPermitId])

  const toggleSort = key => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'validFrom' ? 'desc' : 'asc')
    }
  }

  const clearFilters = () => {
    setSearch('')
    setStatusFilter('all')
    setActivityFilter('all')
    setRiskFilter('all')
  }

  const [transitionTarget, setTransitionTarget] = useState(null)

  const startTransition = (permit, status) => {
    const needsAcceptor = status === 'active' && permit.status === 'approved'
    const needsNote = status === 'closed' || status === 'suspended'
    if (needsAcceptor || needsNote) {
      setTransitionTarget({ permit, status, needsAcceptor, needsNote })
      return
    }
    doTransition(permit, status, {})
  }

  const doTransition = async (permit, status, extra) => {
    setBusy(permit.id)
    try {
      await transitionPermit(bpId, permit.id, { status, ...extra })
      toast.success(`Permit ${STATUS_META[status]?.label?.toLowerCase() || status}.`)
      setTransitionTarget(null)
      await load()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Transition failed.')
    } finally {
      setBusy(null)
    }
  }

  const handleAttachRams = async (permitId, e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || attachingRamsId) return
    if (file.size > 100 * 1024 * 1024) { toast.error('RAMS file must be under 100MB.'); return }
    setAttachingRamsId(permitId)
    try {
      const { s3Key, fileName } = await uploadPermitRamsFile(bpId, file)
      await attachPermitRams(bpId, permitId, { rams_s3_key: s3Key, rams_file_name: fileName })
      toast.success('RAMS attached. The permit can now be approved.')
      await load()
    } catch (err) {
      toast.error('Could not attach the RAMS. Please try again.')
    } finally {
      setAttachingRamsId(null)
    }
  }

  const handleRamsDownload = async (permitId) => {
    try {
      const res = await downloadPermitRams(bpId, permitId)
      const url = res.data?.data?.downloadUrl
      if (url) window.open(url, '_blank', 'noopener')
      else toast.error('No RAMS file to download.')
    } catch (e) {
      toast.error('Could not download the RAMS file.')
    }
  }

  const save = async draft => {
    try {
      await createPermit(bpId, draft)
      toast.success('Permit raised as draft.')
      setCreating(false)
      await load()
    } catch (e) {
      toast.error('Could not raise permit.')
    }
  }

  const saveEdit = async (permitId, payload) => {
    try {
      await updatePermit(bpId, permitId, payload)
      toast.success('Permit updated.')
      setEditing(null)
      await load()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not update the permit.')
    }
  }

  return (
    <ComplianceShell activeTab='permits' bpId={bpId} embedded={embedded} onTabChange={onTabChange} chromeless={chromeless}>
      <Head>
        <HeadText>
          <Eyebrow>Permits to work</Eyebrow>
          <Title>Controlled activities on this building</Title>
          <Subtitle>
            Raise, review, approve and close out permits. High-risk permits require a linked RAMS document before approval. Every transition is recorded for audit.
          </Subtitle>
        </HeadText>
        <PrimaryBtn onClick={() => setCreating(true)} data-tour-id='permits.raise-btn'>
          <AddRoundedIcon style={{ fontSize: 16 }} />
          Raise permit
        </PrimaryBtn>
      </Head>

      {loading ? (
        <>
          <SkToolbar>
            <SkSearch />
            <SkFilters>
              <SkPill $w='150px' />
              <SkPill $w='150px' />
              <SkPill $w='120px' />
            </SkFilters>
          </SkToolbar>
          <SkList>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkRow key={i} style={{ animationDelay: `${i * 60}ms` }}>
                <SkStatus />
                <SkBody>
                  <SkLine $w='40%' />
                  <SkLine $w='65%' />
                </SkBody>
                <SkActions>
                  <SkPill $w='72px' />
                  <SkPill $w='72px' />
                </SkActions>
              </SkRow>
            ))}
          </SkList>
        </>
      ) : permits.length === 0 ? (
        <Empty>No permits raised on this Building Passport yet.</Empty>
      ) : (
        <>
          <Controls>
            <SearchWrap>
              <SearchRoundedIcon style={{ fontSize: 18 }} />
              <SearchInput
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder='Search by title, reference, contractor or activity…'
                aria-label='Search permits' />
            </SearchWrap>
            <Filters>
              <FilterGroup>
                <FilterLabel>Status</FilterLabel>
                <FilterField>
                  <SearchableSelect
                    value={statusFilter}
                    onChange={v => setStatusFilter(v)}
                    options={[{ value: 'all', label: 'All statuses' }, ...statusOptions]}
                    ariaLabel='Filter by status'
                  />
                </FilterField>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel>Activity</FilterLabel>
                <FilterField>
                  <SearchableSelect
                    value={activityFilter}
                    onChange={v => setActivityFilter(v)}
                    options={[{ value: 'all', label: 'All activities' }, ...activityOptions]}
                    searchPlaceholder='Search activities…'
                    ariaLabel='Filter by activity type'
                  />
                </FilterField>
              </FilterGroup>
              {(riskPresent.high || riskPresent.low) && (
                <FilterGroup>
                  <FilterLabel>Risk level</FilterLabel>
                  <RiskChips role='group' aria-label='Filter by risk level'>
                    <FilterChip type='button' $active={riskFilter === 'all'}
                      onClick={() => setRiskFilter('all')}
                      title='Show permits of any risk level'>
                      Any
                    </FilterChip>
                    {riskPresent.high && (
                      <FilterChip type='button' $active={riskFilter === 'high'} $fg={RAG.high.fg} $bg={RAG.high.bg}
                        onClick={() => setRiskFilter('high')}
                        title={RAG.high.hint}>
                        High risk
                      </FilterChip>
                    )}
                    {riskPresent.low && (
                      <FilterChip type='button' $active={riskFilter === 'low'} $fg={RAG.low.fg} $bg={RAG.low.bg}
                        onClick={() => setRiskFilter('low')}
                        title={RAG.low.hint}>
                        Standard
                      </FilterChip>
                    )}
                  </RiskChips>
                </FilterGroup>
              )}
            </Filters>
          </Controls>

          <SortBar role='group' aria-label='Sort permits'>
            <SortLabel>Sort by</SortLabel>
            {SORTS.map(s => (
              <SortBtn
                key={s.key}
                type='button'
                $active={sortKey === s.key}
                onClick={() => toggleSort(s.key)}
                title={`Sort by ${s.title} — click again to reverse the order`}>
                {s.label}
                {sortKey === s.key && <Arrow aria-hidden='true'>{sortDir === 'asc' ? '▲' : '▼'}</Arrow>}
              </SortBtn>
            ))}
          </SortBar>

          <CountLine aria-live='polite'>
            Showing {visible.length} of {filtered.length} {filtered.length === 1 ? 'permit' : 'permits'}
            {filtered.length !== permits.length ? ` (filtered from ${permits.length})` : ''}
          </CountLine>

          {filtered.length === 0 ? (
            <Empty>
              No permits match your search or filters.
              <ClearRow>
                <GhostBtn type='button' onClick={clearFilters}>Clear search and filters</GhostBtn>
              </ClearRow>
            </Empty>
          ) : (
            <List>
              {visible.map(p => {
                const meta = STATUS_META[p.status] || STATUS_META.draft
                return (
                  <Row
                    key={p.id}
                    ref={el => {
                      if (el) rowRefs.current[String(p.id)] = el
                      else delete rowRefs.current[String(p.id)]
                    }}
                    $highlighted={String(p.id) === String(highlightedPermitId)}
                  >
                    <StatusChip $tint={meta.tint} title={meta.hint}>{meta.label}</StatusChip>
                    <Body>
                      <RowTitle>
                        {p.reference && <RefChip>{p.reference}</RefChip>}
                        {p.title}
                        {p.isHighRisk && (
                          <HighRisk title={RAG.high.hint}>
                            <WarningAmberRoundedIcon style={{ fontSize: 12 }} />
                            High risk
                          </HighRisk>
                        )}
                      </RowTitle>
                      <RowMeta>
                        <span>{p.activityTypeLabel}</span>
                        {p.contractor && <span>{p.contractor.name}{p.contractor.company ? ` · ${p.contractor.company}` : ''}</span>}
                        {p.ramsDocument && <RamsChip>RAMS: {p.ramsDocument.displayName}</RamsChip>}
                        {p.ramsFile && (
                          <RamsChip as='button' $clickable type='button' title='Download the attached RAMS' onClick={() => handleRamsDownload(p.id)}>
                            RAMS: {p.ramsFile.fileName}
                          </RamsChip>
                        )}
                        {p.isHighRisk && !p.ramsDocument && !p.ramsFile
                          && (p.status === 'draft' || p.status === 'pending_review') && (
                          <AttachRamsBtn as='label' title='Upload a RAMS document to attach to this permit'>
                            <input type='file' hidden disabled={attachingRamsId === p.id}
                              onChange={e => handleAttachRams(p.id, e)}
                              accept='.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg' />
                            <WarningAmberRoundedIcon style={{ fontSize: 12 }} />
                            {attachingRamsId === p.id ? 'Attaching…' : 'Attach RAMS'}
                          </AttachRamsBtn>
                        )}
                        {p.validFrom && (
                          <span>
                            {new Date(p.validFrom).toLocaleDateString('en-GB')}
                            {p.validTo ? ` → ${new Date(p.validTo).toLocaleDateString('en-GB')}` : ''}
                          </span>
                        )}
                        {p.requestedBy && <span>raised by {hideEmail(p.requestedBy)}</span>}
                        {p.acceptedByName && <span>accepted by {hideEmail(p.acceptedByName)}</span>}
                      </RowMeta>
                      {p.closureNote && <ClosureNote>Closed: {p.closureNote}</ClosureNote>}
                    </Body>
                    <RowActions>
                      {(p.status === 'draft' || p.status === 'pending_review') && (
                        <EditBtn
                          disabled={busy === p.id}
                          onClick={() => setEditing(p)}
                          title='Edit this permit’s details before it goes for approval'>
                          <EditRoundedIcon style={{ fontSize: 14 }} />
                          Edit
                        </EditBtn>
                      )}
                      {(p.allowedTransitions || []).map(next => (
                        <TransitionBtn
                          key={next}
                          $primary={next === 'approved' || next === 'active'}
                          $danger={next === 'cancelled'}
                          disabled={busy === p.id}
                          onClick={() => startTransition(p, next)}>
                          {transitionLabelFor(p, next)}
                        </TransitionBtn>
                      ))}
                      <HistoryBtn
                        as='a'
                        href={`/bp-list/${bpId}/compliance/permits/${p.id}/print`}
                        target='_blank'
                        rel='noopener noreferrer'
                        title='Print permit'>
                        <PrintRoundedIcon style={{ fontSize: 15 }} />
                      </HistoryBtn>
                      <HistoryBtn onClick={() => setHistoryTarget(p)} title='Audit trail'>
                        <HistoryRoundedIcon style={{ fontSize: 15 }} />
                      </HistoryBtn>
                    </RowActions>
                  </Row>
                )
              })}
            </List>
          )}

          <InfiniteScrollSentinel
            hasMore={visibleCount < filtered.length}
            loading={false}
            onLoadMore={() => setVisibleCount(c => c + PAGE)} />
        </>
      )}

      {creating && (
        <PermitModal
          bpId={bpId}
          activityTypes={activityTypes}
          highRiskActivities={highRiskActivities}
          contractors={contractors}
          onClose={() => setCreating(false)}
          onSave={save}
        />
      )}

      {editing && (
        <PermitModal
          bpId={bpId}
          activityTypes={activityTypes}
          highRiskActivities={highRiskActivities}
          contractors={contractors}
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={payload => saveEdit(editing.id, payload)}
        />
      )}

      {transitionTarget && (
        <TransitionModal
          target={transitionTarget}
          busy={busy === transitionTarget.permit.id}
          onClose={() => setTransitionTarget(null)}
          onConfirm={extra => doTransition(transitionTarget.permit, transitionTarget.status, extra)}
        />
      )}

      {historyTarget && (
        <Backdrop onClick={() => setHistoryTarget(null)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <ModalTitle>Audit trail — {historyTarget.title}</ModalTitle>
            <EventList>
              {(historyTarget.events || []).map((e, i) => (
                <EventRow key={i}>
                  <EventName>{STATUS_META[e.event]?.label || e.event}</EventName>
                  <EventMeta>
                    {e.user && <span>{e.user}</span>}
                    {e.createdAt && <span>{new Date(e.createdAt).toLocaleString('en-GB')}</span>}
                  </EventMeta>
                  {e.note && <EventNote>{e.note}</EventNote>}
                </EventRow>
              ))}
            </EventList>
            <ModalActions>
              <GhostBtn onClick={() => setHistoryTarget(null)}>Close</GhostBtn>
            </ModalActions>
          </ModalCard>
        </Backdrop>
      )}
    </ComplianceShell>
  )
}

const TransitionModal = ({ target, busy, onClose, onConfirm }) => {
  const [acceptorName, setAcceptorName] = useState('')
  const [note, setNote] = useState('')
  const { permit, status, needsAcceptor, needsNote } = target
  const heading = status === 'active' ? 'Activate permit'
    : status === 'suspended' ? 'Suspend permit'
    : status === 'closed' ? 'Close out permit'
    : 'Confirm'
  const canConfirm = (!needsAcceptor || acceptorName.trim()) && (!needsNote || status === 'closed' || note.trim())
  return (
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalTitle>{heading} — {permit.reference || permit.title}</ModalTitle>
        <FieldGrid>
          {needsAcceptor && (
            <Field $span>
              <FieldLabel>Accepted by (person carrying out the work)</FieldLabel>
              <Input
                autoFocus
                value={acceptorName}
                onChange={e => setAcceptorName(e.target.value)}
                placeholder='Full name — confirms they accept the permit conditions' />
            </Field>
          )}
          {needsNote && (
            <Field $span>
              <FieldLabel>{status === 'suspended' ? 'Suspension reason' : 'Closure note'}</FieldLabel>
              <TextArea
                rows={3}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={status === 'suspended'
                  ? 'Why is work being stopped?'
                  : 'What was completed / any follow-ups?'} />
            </Field>
          )}
        </FieldGrid>
        <ModalActions>
          <GhostBtn onClick={onClose} disabled={busy}>Cancel</GhostBtn>
          <PrimaryBtn
            disabled={busy || !canConfirm}
            onClick={() => onConfirm({
              ...(needsAcceptor ? { acceptor_name: acceptorName.trim() } : {}),
              ...(note.trim() ? { note: note.trim() } : {}),
            })}>
            {busy ? 'Saving…' : heading}
          </PrimaryBtn>
        </ModalActions>
      </ModalCard>
    </Backdrop>
  )
}

const PermitModal = ({ bpId, activityTypes, highRiskActivities, contractors, initial, onClose, onSave }) => {
  const isEdit = !!initial
  const [draft, setDraft] = useState(() => initial ? {
    title: initial.title || '',
    description: initial.description || '',
    activityType: initial.activityType || 'general',
    contractorId: initial.contractor?.id || '',
    ramsDocumentId: '', ramsS3Key: '', ramsFileName: '',
    validFrom: initial.validFrom ? initial.validFrom.slice(0, 10) : '',
    validTo: initial.validTo ? initial.validTo.slice(0, 10) : '',
  } : {
    title: '', description: '', activityType: 'general',
    contractorId: '', ramsDocumentId: '', ramsS3Key: '', ramsFileName: '', validFrom: '', validTo: '',
  })
  const [uploadingRams, setUploadingRams] = useState(false)
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }))
  const selectedContractor = contractors.find(c => String(c.id) === String(draft.contractorId))
  const ramsOptions = (selectedContractor?.documents || []).filter(d => d.docType === 'rams')
  const isHighRisk = (highRiskActivities || []).includes(draft.activityType)

  const handleRamsUpload = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 100 * 1024 * 1024) { toast.error('RAMS file must be under 100MB.'); return }
    setUploadingRams(true)
    try {
      const { s3Key, fileName } = await uploadPermitRamsFile(bpId, file)
      setDraft(d => ({ ...d, ramsS3Key: s3Key, ramsFileName: fileName, ramsDocumentId: '' }))
    } catch (err) {
      toast.error('Could not upload the RAMS file. Please try again.')
    } finally {
      setUploadingRams(false)
    }
  }

  return (
    <Backdrop onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalTitle>{isEdit ? 'Edit permit' : 'Raise permit to work'}</ModalTitle>
        <FieldGrid>
          <Field $span>
            <FieldLabel>Title</FieldLabel>
            <Input value={draft.title} onChange={e => set('title', e.target.value)}
              placeholder='e.g. Hot works, riser welding, level 3' autoFocus data-tour-id='permitModal.title' />
          </Field>
          <Field data-tour-id='permitModal.activity'>
            <FieldLabel>Activity type</FieldLabel>
            <SearchableSelect
              value={draft.activityType}
              onChange={val => set('activityType', val)}
              options={Object.entries(activityTypes).map(([v, l]) => ({ value: v, label: l }))}
              searchPlaceholder='Search activity types…'
              ariaLabel='Activity type'
            />
          </Field>
          <Field>
            <FieldLabel>Risk level</FieldLabel>
            <RiskLevel $on={isHighRisk} title='Set automatically from the activity type. High-risk activities need a linked RAMS before the permit can be approved.'>
              <WarningAmberRoundedIcon style={{ fontSize: 13 }} />
              {isHighRisk ? 'High risk: RAMS required' : 'Standard risk'}
            </RiskLevel>
            <RiskHint>Set automatically from the activity type</RiskHint>
          </Field>
          <Field>
            <FieldLabel>Contractor</FieldLabel>
            <SearchableSelect
              value={draft.contractorId}
              onChange={val => set('contractorId', val)}
              options={contractors.map(c => ({ value: c.id, label: `${c.name}${c.company ? ` (${c.company})` : ''}` }))}
              clearable
              clearLabel='None / internal'
              placeholder='None / internal'
              searchPlaceholder='Search contractors…'
              ariaLabel='Contractor'
            />
          </Field>
          {isEdit ? (
            <Field $span>
              <FieldLabel>RAMS document</FieldLabel>
              <RiskHint>
                {isHighRisk
                  ? 'This is a high-risk activity, so a RAMS document is required before approval. Attach or replace the RAMS from the permit’s row after saving your changes.'
                  : 'RAMS documents are managed from the permit’s row, not here.'}
              </RiskHint>
            </Field>
          ) : (
            <Field $span>
              <FieldLabel>RAMS document{isHighRisk ? ' (required before approval)' : ''}</FieldLabel>
              {draft.ramsFileName ? (
                <RamsAttached>
                  <TaskAltRoundedIcon style={{ fontSize: 15, color: '#15803d' }} />
                  <span>{draft.ramsFileName}</span>
                  <RemoveRams type='button' onClick={() => setDraft(d => ({ ...d, ramsS3Key: '', ramsFileName: '' }))}>Remove</RemoveRams>
                </RamsAttached>
              ) : (
                <RamsRow>
                  {ramsOptions.length > 0 && (
                    <RamsField>
                      <SearchableSelect
                        value={draft.ramsDocumentId}
                        onChange={val => set('ramsDocumentId', val)}
                        options={ramsOptions.map(d => ({ value: d.id, label: d.displayName }))}
                        placeholder={`Select from ${selectedContractor?.name}…`}
                        searchPlaceholder='Search documents…'
                        ariaLabel='RAMS document'
                      />
                    </RamsField>
                  )}
                  <UploadRams as='label'>
                    <input type='file' hidden disabled={uploadingRams} onChange={handleRamsUpload}
                      accept='.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg' />
                    {uploadingRams ? 'Uploading…' : (ramsOptions.length > 0 ? 'or upload a RAMS' : 'Upload a RAMS')}
                  </UploadRams>
                </RamsRow>
              )}
              <RiskHint>
                {ramsOptions.length > 0
                  ? 'Pick the contractor’s RAMS, or upload one directly.'
                  : 'Upload a RAMS directly, even for internal work with no contractor.'}
              </RiskHint>
            </Field>
          )}
          <Field>
            <FieldLabel>Valid from</FieldLabel>
            <Input type='date' value={draft.validFrom} onChange={e => set('validFrom', e.target.value)} />
          </Field>
          <Field>
            <FieldLabel>Valid to</FieldLabel>
            <Input type='date' value={draft.validTo} onChange={e => set('validTo', e.target.value)} />
          </Field>
          <Field $span>
            <FieldLabel>Description</FieldLabel>
            <TextArea rows={3} value={draft.description} onChange={e => set('description', e.target.value)}
              placeholder='Scope, isolations, precautions…' />
          </Field>
        </FieldGrid>
        <ModalActions>
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn
            disabled={!draft.title.trim()}
            onClick={() => onSave(isEdit ? {
              title: draft.title,
              description: draft.description || null,
              activity_type: draft.activityType,
              contractor_id: draft.contractorId || null,
              valid_from: draft.validFrom || null,
              valid_to: draft.validTo || null,
            } : {
              title: draft.title,
              description: draft.description || null,
              activity_type: draft.activityType,
              is_high_risk: isHighRisk,
              contractor_id: draft.contractorId || null,
              rams_document_id: draft.ramsDocumentId || null,
              rams_s3_key: draft.ramsS3Key || null,
              rams_file_name: draft.ramsFileName || null,
              valid_from: draft.validFrom || null,
              valid_to: draft.validTo || null,
            })}
            data-tour-id='permitModal.submit'>
            {isEdit ? 'Save changes' : 'Raise as draft'}
          </PrimaryBtn>
        </ModalActions>
      </ModalCard>
    </Backdrop>
  )
}

export default PermitsPage

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Head = styled.header`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  animation: ${fadeIn} 280ms ease both;
`
const HeadText = styled.div` display: flex; flex-direction: column; gap: 4px; `
const Eyebrow = styled.span`
  font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
`
const Title = styled.h2`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 24px;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.haiti};
`
const Subtitle = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
  max-width: 70ch;
  line-height: 1.5;
`

const List = styled.div` display: flex; flex-direction: column; gap: 10px; `

const Row = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 14px 18px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  transition: background 260ms ease, border-color 260ms ease, box-shadow 260ms ease;
  ${({ $highlighted }) => $highlighted && css`
    background: #e6f7f9;
    border-color: #33becc;
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.18);
  `}
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
    align-items: start;
  }
`

const StatusChip = styled.span`
  align-self: start;
  padding: 4px 11px;
  background: ${({ $tint }) => `${$tint}14`};
  color: ${({ $tint }) => $tint};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`

const Body = styled.div` display: flex; flex-direction: column; gap: 4px; min-width: 0; `
const RowTitle = styled.span`
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  flex-wrap: wrap;
`
const RefChip = styled.span`
  font-family: monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f4f4f6;
  color: ${({ theme }) => theme.colors.grey[800]};
  border: 1px solid #e4e4e7;
`

const HighRisk = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: #ffedd5;
  color: #c2410c;
  border: 1px solid rgba(194, 65, 12, 0.28);
`
const RowMeta = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px 14px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const RamsChip = styled.span`
  font: inherit;
  color: #1f8c97;
  font-weight: 700;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  text-align: inherit;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:hover { text-decoration: ${({ $clickable }) => ($clickable ? 'underline' : 'none')}; }
`
const AttachRamsBtn = styled.label`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 6px;
  border: 1px dashed #c2410c; background: #fff7ed;
  color: #c2410c; font-size: 11px; font-weight: 700; cursor: pointer;
  &:hover { background: #ffedd5; }
  svg { color: #c2410c; }
`
const ClosureNote = styled.span`
  font-size: 11.5px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const RowActions = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  flex-wrap: wrap;
  justify-content: flex-end;
  @media (max-width: 768px) { justify-content: flex-start; }
`
const TransitionBtn = styled.button`
  font: inherit;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.03em;
  cursor: pointer;
  white-space: nowrap;
  background: ${({ $primary, $danger }) =>
    $primary ? '#33becc'
    : $danger ? 'transparent'
    : '#f4f4f6'};
  color: ${({ $primary, $danger, theme }) =>
    $primary ? '#fff'
    : $danger ? '#b00020'
    : theme.colors.haiti};
  border: 1px solid ${({ $primary, $danger }) =>
    $primary ? 'transparent'
    : $danger ? 'rgba(204,34,0,0.30)'
    : '#e4e4e7'};
  transition: background 160ms ease;
  &:hover:not(:disabled) {
    background: ${({ $primary, $danger }) =>
      $primary ? '#2aa6b3'
      : $danger ? 'rgba(204,34,0,0.06)'
      : '#ececec'};
  }
  &:disabled { opacity: 0.55; cursor: wait; }
`
const EditBtn = styled(TransitionBtn)`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`
const HistoryBtn = styled.button`
  font: inherit;
  width: 28px; height: 28px;
  border-radius: 8px;
  border: 1px solid #ececec;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  &:hover { background: #f4f4f6; color: ${({ theme }) => theme.colors.haiti}; }
`

const Empty = styled.div`
  padding: 36px;
  text-align: center;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 13px;
`

const PrimaryBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 9px 16px;
  background: #33becc;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
  transition: background 160ms ease;
  &:hover:not(:disabled) { background: #2aa6b3; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
const GhostBtn = styled.button`
  font: inherit;
  padding: 9px 16px;
  background: transparent;
  border: 1px solid #ececec;
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #f4f4f6; }
`

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 220;
`
const ModalCard = styled.div`
  background: #fff;
  border-radius: 18px;
  padding: 24px 26px;
  width: min(620px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  display: flex; flex-direction: column; gap: 14px;
  box-shadow: 0 18px 48px rgba(12, 18, 41, 0.28);
`
const ModalTitle = styled.h3`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 22px;
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.haiti};
`
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`
const Field = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  ${({ $span }) => $span ? 'grid-column: 1 / -1;' : ''}
`
const FieldLabel = styled.label`
  font-size: 10.5px; letter-spacing: 0.10em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
`
const Input = styled.input`
  font: inherit;
  padding: 9px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  outline: none;
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  &:focus { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.12); }
`
const TextArea = styled.textarea`
  font: inherit;
  padding: 9px 12px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  resize: vertical;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  &:focus { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.12); }
`
const FilterField = styled.div`
  min-width: 170px;
`
const RamsField = styled.div`
  flex: 1;
  min-width: 180px;
`
const RiskLevel = styled.div`
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  background: ${({ $on }) => $on ? '#ffedd5' : '#dcfce7'};
  color: ${({ $on }) => $on ? '#c2410c' : '#15803d'};
  border: 1px solid ${({ $on }) => $on ? 'rgba(194,65,12,0.30)' : 'rgba(21,128,61,0.28)'};
`
const RiskHint = styled.div`
  margin-top: 5px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const RamsRow = styled.div`
  display: flex; gap: 8px; align-items: stretch; flex-wrap: wrap;
  & > select { flex: 1; min-width: 180px; }
`
const UploadRams = styled.label`
  display: inline-flex; align-items: center; justify-content: center;
  height: 38px; padding: 0 14px; box-sizing: border-box;
  border: 1px dashed #cbd5e1; border-radius: 8px;
  font-size: 12px; font-weight: 600; color: #4a4f5e; background: #fafbfc;
  cursor: pointer; white-space: nowrap;
  &:hover { background: #f1f5f9; border-color: #94a3b8; }
`
const RamsAttached = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  height: 38px; padding: 0 12px; box-sizing: border-box;
  border: 1px solid rgba(21,128,61,0.25); border-radius: 8px; background: #f0fdf4;
  font-size: 12.5px; color: #15803d; max-width: 100%;
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`
const RemoveRams = styled.button`
  font: inherit; margin-left: auto; padding: 3px 8px; border: none; border-radius: 6px;
  background: #fff; color: #b00020; font-size: 11px; font-weight: 700; cursor: pointer;
  &:hover { background: #fdecea; }
`
const ModalActions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end;
`
const EventList = styled.div` display: flex; flex-direction: column; gap: 8px; `
const EventRow = styled.div`
  padding: 10px 12px;
  background: #fafbfc;
  border: 1px solid #f0f0f3;
  border-radius: 10px;
  display: flex; flex-direction: column; gap: 2px;
`
const EventName = styled.span`
  font-size: 12.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
  text-transform: capitalize;
`
const EventMeta = styled.span`
  display: inline-flex; gap: 12px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const EventNote = styled.span`
  font-size: 12px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-top: 4px;
`

const Controls = styled.div`
  display: flex; align-items: flex-end; justify-content: space-between; gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`
const SearchWrap = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  height: 40px; padding: 0 14px; box-sizing: border-box;
  flex: 1 1 300px; min-width: 240px; max-width: 460px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.grey[800]};
  transition: border-color 140ms ease, box-shadow 140ms ease;
  &:focus-within { border-color: #33becc; box-shadow: 0 0 0 3px rgba(51,190,204,0.12); }
  svg { flex: none; }
`
const SearchInput = styled.input`
  font: inherit;
  border: none; outline: none; background: transparent;
  width: 100%;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  &::placeholder { color: #9aa0aa; }
`
const Filters = styled.div`
  display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
`
const FilterGroup = styled.div`
  display: flex; flex-direction: column; gap: 5px;
`
const FilterLabel = styled.span`
  font-size: 10.5px; letter-spacing: 0.10em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
`
const RiskChips = styled.div`
  display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap;
  min-height: 38px;
`
const FilterChip = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  height: 38px; padding: 0 14px;
  border-radius: 999px;
  font-size: 12.5px; font-weight: 700;
  cursor: pointer; white-space: nowrap;
  border: 1px solid ${({ $active, $fg }) => ($active && $fg ? $fg : $active ? '#33becc' : '#e4e4e7')};
  background: ${({ $active, $bg }) => ($active && $bg ? $bg : $active ? '#e6f7f9' : '#fff')};
  color: ${({ $active, $fg, theme }) => ($active && $fg ? $fg : $active ? '#2aa6b3' : theme.colors.grey[800])};
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
  &:hover { border-color: ${({ $active, $fg }) => ($active && $fg ? $fg : '#33becc')}; }
`
const SortBar = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 8px 10px;
  margin-bottom: 10px;
  background: #fafbfc;
  border: 1px solid #f0f0f3;
  border-radius: 12px;
`
const SortLabel = styled.span`
  font-size: 10.5px; letter-spacing: 0.10em; text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]}; font-weight: 800;
  margin-right: 2px;
`
const SortBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  height: 34px; padding: 0 12px;
  border-radius: 8px;
  font-size: 12.5px; font-weight: 700;
  cursor: pointer; white-space: nowrap;
  border: 1px solid ${({ $active }) => ($active ? '#33becc' : '#e4e4e7')};
  background: ${({ $active }) => ($active ? '#e6f7f9' : '#fff')};
  color: ${({ $active, theme }) => ($active ? '#2aa6b3' : theme.colors.haiti)};
  transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
  &:hover { border-color: #33becc; }
`
const Arrow = styled.span`
  font-size: 11px; line-height: 1;
`
const CountLine = styled.div`
  font-size: 12.5px; font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin: 0 2px 12px;
`
const ClearRow = styled.div`
  margin-top: 12px;
  display: flex; justify-content: center;
`

const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
`
const shimmer = css`
  background: #e7e9ee;
  border-radius: 6px;
  animation: ${skeletonPulse} 1.3s ease-in-out infinite;
`
const SkToolbar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`
const SkSearch = styled.span`
  ${shimmer}
  height: 40px; width: 320px; max-width: 60%;
  border-radius: 999px;
`
const SkFilters = styled.div`
  display: inline-flex; gap: 10px; flex-wrap: wrap;
`
const SkPill = styled.span`
  ${shimmer}
  height: 34px; width: ${({ $w }) => $w || '120px'};
  border-radius: 999px;
`
const SkList = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`
const SkRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 14px; align-items: center;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  animation: ${fadeIn} 0.4s ease both;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`
const SkStatus = styled.span`
  ${shimmer}
  width: 96px; height: 22px;
  border-radius: 999px;
`
const SkBody = styled.div`
  display: flex; flex-direction: column; gap: 8px; min-width: 0;
`
const SkLine = styled.span`
  ${shimmer}
  height: 11px; width: ${({ $w }) => $w || '100%'};
`
const SkActions = styled.div`
  display: inline-flex; gap: 6px; justify-content: flex-end;
`
